import { useEffect, useMemo, useState } from 'react';

// Components
import { ConfigContext, Header, PillTabs, SizeProvider } from '@velinsi/smp-widgets-ui';
import TeamSquadView from '../../components/TeamSquadView';

// Hooks
import useTeamSquad from '../../hooks/useTeamSquad';

// Normalizers
import { normalizeTeamSquadData } from '../../normalizers/normalizeTeamSquadResponseData';

// Providers
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';

// Types
import { BaseWidgetProps, INormalizedStandingsGroup, ITeamSquadConfig } from '../../../../../packages/types';
import { INormalizedTeamSquadGridItem } from '../../types';
import { defaultHandlePlayerClick, defaultHandleTeamClick } from '@sportal365/smp-widget-utils';
import { NATIONAL } from '../../../../../packages/constants/sportRegions';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';

const LAYOUT_VIEW_CHANGE = 768;

export enum TeamSquadViewEnums {
  LIST = 'list',
  GRID = 'grid',
}

export type TeamSquadWidgetProps = BaseWidgetProps<ITeamSquadConfig>;

export const TeamSquadWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: TeamSquadWidgetProps) => {
  const { ref } = useRootNodeRef();
  const [seasonId, setSeasonId] = useState<string | undefined>(config?.dataSeason);
  const [view, setView] = useState<string>(config?.dataHeaderDefaultOption || TeamSquadViewEnums.LIST);
  const [initialLoad, setInitialLoad] = useState(true);

  const {
    showWidget,
    handleClick,
    widgetData: { teamSquad, seasons, isLoading },
  } = useTeamSquad({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataTeam: config?.dataTeam,
    dataSeason: seasonId,
    dataCompetition: config?.dataCompetition,
    dataWidgetCuid: cuid,
  });

  useEffect(() => {
    if (initialLoad) {
      const isCurrentlyMobile = ref && ref.current && ref.current?.offsetWidth < LAYOUT_VIEW_CHANGE;
      if (config?.dataHeaderDefaultOption) {
        setView(config?.dataHeaderDefaultOption);
      } else {
        setView(isCurrentlyMobile ? TeamSquadViewEnums.GRID : TeamSquadViewEnums.LIST);
      }
      setInitialLoad(false);
    }
  }, [initialLoad, config?.dataHeaderDefaultOption, ref]);

  const options = useMemo(
    () =>
      seasons &&
      seasons.map((season) => ({
        label: `${season.name} ${season.tournament?.name}`,
        value: season.id,
      })),
    [seasons],
  );

  if (!showWidget) return null;

  const normalizedTeamSquadData =
    teamSquad &&
    normalizeTeamSquadData({
      squadData: teamSquad,
      dataEntityLinks: config?.dataEntityLinks,
      dataTeamNameType: config?.dataTeamNameType,
      dataLabels: config?.dataLabels,
      dataElements: config?.dataElements,
      displayImage: config?.dataImageDisplay,
    });

  const handleSeasonChange = (value?: string) => {
    setSeasonId(value);
  };

  const handlePlayerClick = (id: string | number, name: string) => defaultHandlePlayerClick(handleClick, id, name);

  const handleTeamClick = () =>
    defaultHandleTeamClick(handleClick, {
      id: normalizedTeamSquadData?.team?.id ?? '',
      name: normalizedTeamSquadData?.team?.name ?? '',
    });

  const handleChangeView = (view: string) => {
    setView(view);
  };

  const tabOptions = [
    { name: config?.dataLabels?.list_view ?? 'List view', value: TeamSquadViewEnums.LIST },
    { name: config?.dataLabels?.card_view ?? 'Card view', value: TeamSquadViewEnums.GRID },
  ];

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <ThemeProvider
        isLoading={isLoading}
        shadow={shadow}
        themes={themes}
        css={{ backgroundColor: '$sportEntityContainerPrimaryBgColor', pb: '$2' }}
        dataTheme={config?.dataTheme}
        refForShadow={ref}
      >
        <SizeProvider>
          <Header
            secondaryHeader={
              config?.dataOptionsDisplay ? (
                <PillTabs
                  tabs={tabOptions || []}
                  selectedTab={view || ''}
                  handleTabClick={handleChangeView}
                  handleSubscriptionClick={handleClick}
                />
              ) : undefined
            }
            logo={normalizedTeamSquadData?.team?.logo}
            link={normalizedTeamSquadData?.teamLink}
            urlTarget={normalizedTeamSquadData?.urlTarget}
            title={normalizedTeamSquadData?.team?.name}
            selectedOption={seasonId || seasons?.[0]?.id}
            options={options}
            handleChange={handleSeasonChange}
            handleClick={handleTeamClick}
            handleSubscriptionClick={handleClick}
            isMainImageRound={normalizedTeamSquadData?.team.type === NATIONAL}
            displayMainHeader={config?.dataHeaderDisplay === true}
          />

          <TeamSquadView
            view={view}
            items={
              view === TeamSquadViewEnums.LIST
                ? (normalizedTeamSquadData?.items as {
                    keepers: { name: string; data: INormalizedStandingsGroup[] };
                    defenders: { name: string; data: INormalizedStandingsGroup[] };
                    midfielders: { name: string; data: INormalizedStandingsGroup[] };
                    forwards: { name: string; data: INormalizedStandingsGroup[] };
                  })
                : (normalizedTeamSquadData?.teamSquadDataGridView as {
                    keepers: { name: string; data: INormalizedTeamSquadGridItem[] };
                    defenders: { name: string; data: INormalizedTeamSquadGridItem[] };
                    midfielders: { name: string; data: INormalizedTeamSquadGridItem[] };
                    forwards: { name: string; data: INormalizedTeamSquadGridItem[] };
                  })
            }
            handleClick={handlePlayerClick}
            dataImageDisplay={config?.dataImageDisplay}
          />
        </SizeProvider>
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};
